// Shared types for the mini-game engine (Dokument/spel-spec.md). Imported by
// both server (engine, API routes) and client (teacher view, projector,
// student view) — keep this file free of server-only imports.

import type { Niva } from "@/lib/progress";

export type SpelTyp = "matteopardy" | "stegkampen" | "summario" | "mattehjulet" | "pacman" | "quiz";
/** Games where every group picks a Steg and plays that Steg's question —
 *  one question per Steg per round (Stegkampen, Summario). */
export type StegSpelTyp = "stegkampen" | "summario" | "pacman";

export function arStegSpel(t: SpelTyp): t is StegSpelTyp {
  return t === "stegkampen" || t === "summario" || t === "pacman";
}
/** Standings on the projector and student devices: everyone, or the top three
 *  (a student always also sees their own group). The teacher always sees all. */
export type Stallningsvisning = "alla" | "topp3";
export type SpelStatus = "lobby" | "pagar" | "klar" | "avbruten";
export type GruppForm = "individ" | "slump" | "platsplacering";

/** One delavsnitt on the board (a Matte-opardy column). */
export interface SpelDelavsnitt {
  kursKod: string; // canonical (MATE1A00X …)
  kapitelId: string;
  avsnittId: string;
  slug: string;
  namn: string;
}

export interface SpelConfig {
  delavsnitt: SpelDelavsnitt[];
  varden: Record<Niva, number>;
  gruppForm: GruppForm;
  /** Group size when gruppForm = "slump". */
  gruppStorlek: number;
  /** Invited student ids; null = the whole class. */
  inbjudna: string[] | null;
  /** All games. Missing on sessions created before 2026-09-23 ⇒ "alla". */
  visaStallning?: Stallningsvisning;
  /** Stegkampen/Summario: number of rounds the teacher chose. Matte-opardy:
   *  rounds before the game ends, set only when fewer than the board's squares. */
  antalOmgangar?: number;
  /** Timed game (all games): the play time in minutes, instead of a number of rounds (spel-spec §28). The last
   *  round starts when three thinking times are left. Always set for a vote. */
  tidMinuter?: number;
  /** PacMathMan only: count points (the number of other pacmen a group has eaten). Missing ⇒ no points. */
  pacmanPoang?: boolean;
  /** All games: sound effects on the projector, chosen at start (the running
   *  value lives in GemensamState.ljud). Missing ⇒ on. */
  ljud?: boolean;
  /** All games: background music on the projector, chosen at start (the
   *  running value lives in GemensamState.musik). Missing ⇒ on. */
  musik?: boolean;
  /** All games: seconds per question (TID_MIN–TID_MAX in TID_STEG steps),
   *  the starting value of GemensamState.fragaTid. Missing on sessions
   *  created before 2026-09-24 ⇒ no time limit. */
  tidPerFraga?: number;
  /** Stegkampen/Summario: Steg the teacher closed before the game — no
   *  questions are drawn for them, so no group can pick them. Never all three. */
  lastaSteg?: Niva[];
  /** The students vote which game to play (spel-spec §20). Missing ⇒ the
   *  teacher chose the game. */
  rostning?: SpelRostning;
}

/**
 * A game the students vote on (Erik 2026-09-25). Each student picks a game
 * when joining; the teacher closes the vote in the lobby and the most votes
 * win (a tie or no votes ⇒ drawn among the top). Until then the session's
 * spelTyp is only a placeholder (the first alternative) and no questions
 * are drawn; closing the vote turns the session into the winning game.
 */
export interface SpelRostning {
  alternativ: SpelTyp[];
  /** null while the vote is open. */
  vinnare: SpelTyp | null;
  /** Final count per alternative, set with `vinnare`. */
  resultat?: Partial<Record<SpelTyp, number>>;
  /** The winner was drawn among tied alternatives (or nobody voted). */
  lottad?: boolean;
}

/** Delavsnitt per game: Opardy's board has exactly five columns. */
export const ANTAL_DA: Record<SpelTyp, { min: number; max: number }> = {
  matteopardy: { min: 5, max: 5 },
  stegkampen: { min: 1, max: 10 },
  summario: { min: 1, max: 10 },
  // One slice per delavsnitt; fewer than five makes a dull wheel (Erik 2026-09-30).
  mattehjulet: { min: 5, max: 10 },
  pacman: { min: 1, max: 10 },
  // Vanligt Quiz: a delavsnitt is drawn from the chosen ones every round.
  quiz: { min: 1, max: 10 },
};

/** Default points per Steg (Opardy: square values; Stegkampen: right answer). */
export const STANDARD_VARDEN: Record<SpelTyp, Record<Niva, number>> = {
  matteopardy: { E: 100, C: 200, A: 300 },
  stegkampen: { E: 10, C: 13, A: 15 },
  // Summario scores on the board (spel-spec §22c), not per Steg: unused.
  summario: { E: 0, C: 0, A: 0 },
  // Matte-hjulet: 5 p per right answer whatever the Steg (spel-spec §25).
  mattehjulet: { E: 5, C: 5, A: 5 },
  // Pacman has no points at all (spel-spec §26): unused.
  pacman: { E: 0, C: 0, A: 0 },
  // Vanligt Quiz (Erik 2026-10-03): 10 / 5 / 3 points per right answer on Steg I / II / III — literally as
  // asked (the easiest Steg pays most); the teacher can change them in the start form.
  quiz: { E: 10, C: 5, A: 3 },
};

/** Every game that can be voted on. */
export const ALLA_SPEL: SpelTyp[] = ["matteopardy", "stegkampen", "summario", "mattehjulet", "pacman", "quiz"];

/** A voted game's delavsnitt must suit every alternative. */
export function antalDaFor(alternativ: readonly SpelTyp[]): { min: number; max: number } {
  return {
    min: Math.max(...alternativ.map((t) => ANTAL_DA[t].min)),
    max: Math.min(...alternativ.map((t) => ANTAL_DA[t].max)),
  };
}

/** True while the students are still voting. */
export function rostningOppen(config: Pick<SpelConfig, "rostning">): boolean {
  return !!config.rostning && config.rostning.vinnare === null;
}

/** Time per question in every game: 10 s to 2 min in 10-second steps. */
export const TID_MIN = 10;
export const TID_MAX = 120;
export const TID_STEG = 10;
export const STANDARD_TID = 60;

/** A valid time per question, or null for anything outside the allowed set. */
export function giltigTid(x: unknown): number | null {
  if (typeof x !== "number" || !Number.isInteger(x)) return null;
  if (x < TID_MIN || x > TID_MAX || x % TID_STEG !== 0) return null;
  return x;
}

/** "45 s", "1 min", "1 min 30 s". */
export function formatTid(sekunder: number): string {
  const min = Math.floor(sekunder / 60);
  const s = sekunder % 60;
  if (min === 0) return `${s} s`;
  return s === 0 ? `${min} min` : `${min} min ${s} s`;
}

// ---------------------------------------------------------------------------
// Matte-opardy state (SpelSession.state)
// ---------------------------------------------------------------------------

export type OpardyFas = "lobby" | "valj" | "satsa" | "fraga" | "facit" | "klar";

export interface SpelGrupp {
  /** Display name. Individual play: null → the member's nickname is shown. */
  namn: string | null;
  deltagare: string[]; // SpelDeltagare ids
  /** Team games: the name the students gave the team (shown instead of
   *  `namn`, see lib/spel/lagnamn.ts). */
  lagnamn?: string;
  /** The teacher reset the team's name: it stays `namn` ("Lag N"). */
  lagnamnAterstallt?: boolean;
  /** Team games: the member whose device acts for the team (lib/spel/ledare.ts). */
  ledare?: string;
  /** Votes for leader while none is chosen: voter id → candidate id. */
  ledarRoster?: Record<string, string>;
}

export interface GruppUtfall {
  gruppIndex: number;
  /** The group's (majority) answer, null when nobody in the group answered. */
  svar: string | null;
  ratt: boolean | null;
  tidMs: number | null;
  insats: number;
  delta: number;
}

/** What every game's state has — standings and groups work on this. */
export interface GemensamState {
  fas: string;
  /** Increments every time a question opens. 0 before the first question. */
  runda: number;
  grupper: SpelGrupp[];
  poang: number[];
  /** Points won/lost per group and nivå — the per-nivå standings. */
  poangPerNiva: Record<Niva, number>[];
  /** Sound effects on the projector; the teacher toggles it mid-game.
   *  Missing (sessions before 2026-09-24) ⇒ on. */
  ljud?: boolean;
  /** Background music on the projector; the teacher toggles it mid-game,
   *  independently of the sound effects (`ljud`). Missing ⇒ on. */
  musik?: boolean;
  /** Seconds to answer the next/current question; the teacher may change it
   *  before each question. null/missing ⇒ no time limit. */
  fragaTid?: number | null;
  /** Students the teacher removed from this game — they cannot join again. */
  utkastade?: string[];
  /** Timed game: epoch ms when the play time runs out (set when the game starts). Missing ⇒ not timed. */
  slutVid?: number | null;
  /** Timed game: the round under way (or the next one) is the last. */
  sistaOmgang?: boolean;
}

export interface OpardyState extends GemensamState {
  fas: OpardyFas;
  /** Timed game: the game ends after this question number (`runda`) — the last question. */
  sistaRunda?: number;
  /** Group that picks the next square. */
  valjare: number;
  /** Groups still to choose in the current cycle, in drawn order (every
   *  group chooses once before any chooses again). Missing on sessions
   *  before 2026-09-28 ⇒ a new cycle is drawn. */
  valjarKo?: number[];
  /** Key of the square in play, `${daIndex}-${niva}`. */
  aktivRuta: string | null;
  forbrukade: string[];
  insatser: number[];
  /** Which groups have pressed Satsa this round (the projector moves them
   *  over as they do). Display only — the bet itself is `insatser`.
   *  Missing on sessions before 2026-09-24. */
  satsat?: boolean[];
  /** The round's bet is an extra on top of the square's value (rule from
   *  2026-09-28), set when the square is chosen. Missing ⇒ a round begun
   *  before that deploy: the bet was the whole stake, scored ±bet. */
  insatsTillagg?: boolean;
  /** Epoch ms when the current question opened. */
  fragaOppnad: number | null;
  utfall: GruppUtfall[] | null;
  /** Group that answered first and correctly in the last round. */
  forstRatt: number | null;
}

// ---------------------------------------------------------------------------
// Stegkampen state — "Kahoot with a twist" (spel-spec §16): every round a
// delavsnitt is drawn and announced, each group picks a Steg (I/II/III) and a
// bet, answers that Steg's question; right = Steg points + 2 × bet, wrong or
// no answer = −bet.
// ---------------------------------------------------------------------------

export type StegkampFas = "lobby" | "val" | "fraga" | "facit" | "klar";

export interface StegkampVal {
  niva: Niva;
  insats: number;
  /** The group saved its choice (or pressed Redo) this round. A group that
   *  has not when the question opens plays the lowest open Steg with no bet.
   *  Missing on sessions before 2026-09-24 ⇒ treated as chosen. */
  klar?: boolean;
  /** Epoch ms when the group first became ready this round — orders the
   *  projector's "ready" list. */
  klarVid?: number;
}

export interface StegkampUtfall {
  gruppIndex: number;
  niva: Niva;
  insats: number;
  /** The group's (majority) answer, null when nobody answered. */
  svar: string | null;
  ratt: boolean | null;
  delta: number;
}

export interface StegkampState extends GemensamState {
  fas: StegkampFas;
  /** 1-based round number; 0 before the game starts. */
  omgang: number;
  antalOmgangar: number;
  /** Delavsnitt of the current round (index into config.delavsnitt). */
  daIndex: number | null;
  /** Delavsnitt already drawn in the current cycle (no repeat until all used). */
  dragnaDa: number[];
  /** Each group's choice for the current round. */
  val: StegkampVal[];
  fragaOppnad: number | null;
  utfall: StegkampUtfall[] | null;
}

// ---------------------------------------------------------------------------
// Summario state — a Mario Party–style board game (spel-spec §22): every round
// each group picks a Steg and a direction, answers that Steg's question; a
// right answer rolls steps in the Steg's range (I 1–3, II 2–5, III 3–6), a
// wrong one stumbles one step in a random direction; the pawn walks the board
// ("rakt fram", lib/spel/summario/brade.ts) and collects the node it lands on.
// ---------------------------------------------------------------------------

export type SummarioFas = "lobby" | "val" | "fraga" | "facit" | "drag" | "klar";

export type PowerupTyp = "dubbla" | "extraSteg" | "byt" | "stjal" | "femtio" | "slump" | "teleport";
/** Powerups that wait for the group's choice (with a default after the window). */
export type ValPowerup = "extraSteg" | "byt" | "stjal";

export interface SummarioVal {
  niva: Niva;
  /** Unused since 2026-10-03 (the route is planned after the dice, spel-spec §22k); always null. */
  riktning: string | null;
  /** The group saved its choice this round (see StegkampVal.klar). */
  klar: boolean;
  /** Epoch ms when the group first became ready — orders the "ready" list. */
  klarVid?: number;
}

export interface SummarioPjas {
  /** Node the pawn stands on. */
  nod: string;
  /** Node it last came from ("straight on" for the default direction); null at the start. */
  fran: string | null;
}

export interface SummarioUtfall {
  gruppIndex: number;
  niva: Niva;
  /** The group's (majority) answer, null when nobody answered (masked for students). */
  svar: string | null;
  ratt: boolean | null;
  /** Steps the group walks this round — rolled when the facit opens (right: the Steg's range, else 1), so the
   *  group can plan its route before "Kör". Missing on rounds rolled before 2026-10-03. */
  steg?: number;
}

/** A group's planned route as the views carry it (the route lives in SpelPlan rows, not in the state —
 *  spel-spec §22k / §18). Teacher and projector get `antal` + `klar` for every group; a student only the own
 *  group's, with the route. */
export interface SummarioPlanVy {
  gruppIndex: number;
  /** Nodes planned so far. */
  antal: number;
  /** The group pressed Klar. */
  klar: boolean;
  /** The planned nodes — own group only. */
  vag?: string[];
}

/** One group's movement this round. */
export interface SummarioFlytt {
  gruppIndex: number;
  /** Right answer: rolled in the Steg's range. Otherwise 1 (a stumble). */
  steg: number;
  /** True when the steps were rolled (right answer); false = stumbled one step in a random direction. */
  slagen: boolean;
  /** Node ids walked in order, the last one is the landing node (length = steg). */
  vag: string[];
  /** Node the pawn stood on before the walk. Missing on rounds rolled before
   *  2026-09-28. */
  fran?: string;
}

/**
 * What happened after the dice, in playing order — the projector animates
 * the list; `poang` is every group's points AFTER the event, so standings can
 * follow along without recomputing anything.
 */
export type SummarioHandelse = { nr: number; poang: number[] } & (
  /** `delta` = all points picked up on the walk; `noder` = how many nodes paid (missing on old rounds). */
  | { typ: "poang"; gruppIndex: number; nod: string; delta: number; noder?: number }
  /** Landed on a powerup (announced before its effect). */
  | { typ: "powerup"; gruppIndex: number; nod: string; powerup: PowerupTyp }
  | { typ: "dubbla"; gruppIndex: number; delta: number }
  | { typ: "femtio"; gruppIndex: number; delta: number }
  | { typ: "slump"; gruppIndex: number; delta: number }
  | { typ: "teleport"; gruppIndex: number; fran: string; till: string }
  /** `valt` = the group chose itself (false = the default after the window). */
  | { typ: "extraSteg"; gruppIndex: number; steg: number; vag: string[]; valt: boolean }
  /** mal null = chose not to swap. */
  | { typ: "byt"; gruppIndex: number; mal: number | null; valt: boolean }
  | { typ: "stjal"; gruppIndex: number; mal: number | null; delta: number; valt: boolean }
  /** A used powerup reappeared somewhere else on the board. */
  | { typ: "nyPowerup"; nod: string; powerup: PowerupTyp }
);

export interface SummarioVantande {
  gruppIndex: number;
  powerup: ValPowerup;
}

export interface SummarioDrag {
  /** Epoch ms when the dice were rolled. */
  start: number;
  flyttar: SummarioFlytt[];
  handelser: SummarioHandelse[];
  /** Every group's points before the dice (the replay's standings start
   *  here). Missing on rounds rolled before 2026-09-28. */
  startPoang?: number[];
  /** Choices still open (one shared window, see `deadline`). */
  vantar: SummarioVantande[];
  /** Epoch ms after which open choices get their default: start + the
   *  movement's animation length + the choice window (15 s). */
  deadline: number | null;
}

export interface SummarioState extends GemensamState {
  fas: SummarioFas;
  /** 1-based round number; 0 before the game starts. */
  omgang: number;
  antalOmgangar: number;
  /** Delavsnitt of the current round (index into config.delavsnitt). */
  daIndex: number | null;
  dragnaDa: number[];
  /** Board id (lib/spel/summario/brade.ts). */
  bradeId: string;
  /** Points per node id — every node except the sun. Fixed for the game. */
  nodPoang: Record<string, number>;
  /** Powerups on the board: node id → type. */
  powerups: Record<string, PowerupTyp>;
  /** One pawn per group (same index as grupper). */
  pjaser: SummarioPjas[];
  val: SummarioVal[];
  fragaOppnad: number | null;
  utfall: SummarioUtfall[] | null;
  /** The movement of the current round; null outside "drag". */
  drag: SummarioDrag | null;
}

// ---------------------------------------------------------------------------
// Matte-hjulet state — a wheel of fortune (spel-spec §25): one slice per
// delavsnitt, every group has a fixed marker around the rim; the teacher spins,
// each group gets the slice under its marker and answers one question per
// Steg (I/II/III) of that delavsnitt on its own device. 5 p per right answer,
// the slice's modifier (×2/×3/+5/+10/+20) applies to the round's sum.
// ---------------------------------------------------------------------------

export type HjuletFas = "lobby" | "hjul" | "fraga" | "facit" | "klar";

export type HjulModifierare = { typ: "gange"; varde: 2 | 3 } | { typ: "plus"; varde: 5 | 10 | 20 };

/** Where one group ended up when the wheel stopped. */
export interface HjulLandning {
  gruppIndex: number;
  /** Slice under the group's marker when the wheel stopped. */
  traff: number;
  /** Slices hopped onto after `traff`, in order — the last one is where the
   *  group plays. Empty when `traff` itself had questions left. */
  hopp: number[];
  /** Hop direction: 1 = clockwise, -1 = counter-clockwise; null = no hop. */
  riktning: 1 | -1 | null;
  /** The slice (= index into config.delavsnitt) the group plays; null = the
   *  group has no questions left anywhere and sits the round out. */
  daIndex: number | null;
  /** 0-based visit number on that delavsnitt — picks the questions. */
  besok: number;
  /** The played slice's modifier this round. */
  modifierare: HjulModifierare | null;
}

export interface HjulUtfall {
  gruppIndex: number;
  /** Per Steg: right/wrong, null = no question or no answer. */
  ratt: Record<Niva, boolean | null>;
  /** 5 p per right answer, before the modifier. */
  bas: number;
  modifierare: HjulModifierare | null;
  delta: number;
}

export interface HjulSnurr {
  /** Epoch ms when the spin started. */
  start: number;
  /** Wheel angle (degrees, clockwise) before and after the spin. */
  fran: number;
  till: number;
}

export interface HjuletState extends GemensamState {
  fas: HjuletFas;
  /** 1-based round number; 0 before the game starts. `runda` (GemensamState)
   *  follows it — SpelSvar rows use hjulSvarsRunda(runda, niva). */
  omgang: number;
  antalOmgangar: number;
  /** Marker per group: position around the rim as a fraction of a turn,
   *  clockwise from the top (0 ≤ x < 1). Fixed for the game. */
  platser: number[];
  /** Modifier per slice for the current round (index = daIndex). */
  modifierare: (HjulModifierare | null)[];
  /** Current wheel angle in degrees, clockwise (grows with every spin). */
  vinkel: number;
  /** The current round's spin; null before it. */
  snurr: HjulSnurr | null;
  /** Visits per group per slice: besok[gruppIndex][daIndex]. */
  besok: number[][];
  /** Per group (same index as grupper); null = empty group / not spun yet. */
  landningar: (HjulLandning | null)[] | null;
  /** When answering opens: spin + hops + a short pause after the spin start. */
  fragaOppnad: number | null;
  utfall: HjulUtfall[] | null;
  /** Slices that were used up and replaced by a fresh delavsnitt before this round (spel-spec §25a) — shown on the
   *  wheel for the round. */
  ersatta?: HjulErsattSkiva[];
  /** Every delavsnitt (composite key kapitel/avsnitt/slug) that has been on the wheel, so a replacement is always a new one. */
  anvanda?: string[];
}

export interface HjulErsattSkiva {
  /** Slice index (= daIndex). */
  skiva: number;
  /** Names of the delavsnitt that went and the one that came. */
  fran: string;
  till: string;
}

// ---------------------------------------------------------------------------
// Vanligt Quiz state (spel-spec §27): every round a delavsnitt is drawn from the
// teacher's chosen ones; every group gets the same three questions (Steg I, II,
// III) and answers as many as it has time for, in any order. Right answer =
// the Steg's points (10 / 5 / 3). Nothing else — no bets, no board.
// ---------------------------------------------------------------------------

export type QuizFas = "lobby" | "fraga" | "facit" | "klar";

export interface QuizUtfall {
  gruppIndex: number;
  /** Per Steg: right/wrong; null = no question on that Steg or no answer. */
  ratt: Record<Niva, boolean | null>;
  /** Points won this round. */
  delta: number;
}

export interface QuizState extends GemensamState {
  fas: QuizFas;
  /** 1-based round number; 0 before the game starts. `runda` (GemensamState) follows it —
   *  SpelSvar rows use hjulSvarsRunda(runda, niva), three answers per participant. */
  omgang: number;
  antalOmgangar: number;
  /** Delavsnitt of the current round (index into config.delavsnitt). */
  daIndex: number | null;
  dragnaDa: number[];
  /** Epoch ms when the round's questions opened. */
  fragaOppnad: number | null;
  utfall: QuizUtfall[] | null;
}

// ---------------------------------------------------------------------------
// Pacman state — a maze game (spel-spec §26): every group is a coloured pacman
// in a random maze without dead ends. Each round the group picks a Steg and
// answers; a right answer gives 3/4/5 moves (Steg I/II/III), a wrong one 1.
// Each move is "framåt", "höger" or "vänster" (a turn needs an opening). All
// pacmen then walk at once; power-ups (x, y, z) and eating another pacman
// give extra moves NEXT round. There are no points — it is played for fun.
// ---------------------------------------------------------------------------

export type PacmanFas = "lobby" | "val" | "fraga" | "facit" | "drag" | "klar";

/** 0 = north, 1 = east, 2 = south, 3 = west. */
export type PacmanDir = 0 | 1 | 2 | 3;
/** One planned move relative to the pacman's facing: forward, right, left. */
export type PacmanSvang = "F" | "H" | "V";
export type PacmanPowerupTyp = "x" | "y" | "z";

export interface PacmanLabyrint {
  /** The maze is n × n cells. */
  n: number;
  /** Per cell (index y * n + x): bitmask of open sides, N=1, E=2, S=4, W=8. */
  oppen: number[];
}

export interface PacmanPjas {
  x: number;
  y: number;
  dir: PacmanDir;
}

export interface PacmanVal {
  niva: Niva;
  /** The group saved its Steg this round (see StegkampVal.klar). */
  klar: boolean;
  klarVid?: number;
}

/** Where a number of moves came from — shown on the result screen. */
export type PacmanStegKalla = "svar" | "fel" | "x" | "y" | "z" | "atit";
export interface PacmanStegPost {
  kalla: PacmanStegKalla;
  steg: number;
}

/** A group's plan as the views carry it (the plan lives in SpelPlan rows, not in the state —
 *  spel-spec §26 / §18). The teacher and the projector get `antal` + `klar` for every group;
 *  a student only gets the own group's, with the moves. */
export interface PacmanPlanVy {
  gruppIndex: number;
  /** Moves planned so far. */
  antal: number;
  /** The group pressed Redo. */
  klar: boolean;
  /** The moves themselves — own group only. */
  rorelser?: PacmanSvang[];
}

export interface PacmanUtfall {
  gruppIndex: number;
  niva: Niva;
  /** The group's (majority) answer, null when nobody answered (masked for students). */
  svar: string | null;
  ratt: boolean | null;
}

/** Fun counters shown on the final screen — never a ranking. */
export interface PacmanStat {
  slukade: number;
  uppatna: number;
  powerups: number;
  steg: number;
}

export type PacmanPos = { x: number; y: number };
/** What happened while the pacmen walked, in order; events with the same `k`
 *  (the move number, 0-based) are simultaneous. */
export type PacmanHandelse =
  | { typ: "steg"; k: number; gruppIndex: number; fran: PacmanPjas; till: PacmanPjas }
  | { typ: "powerup"; k: number; gruppIndex: number; x: number; y: number; powerup: PacmanPowerupTyp }
  | { typ: "ata"; k: number; gruppIndex: number; offer: number; x: number; y: number }
  /** Head-on: this pacman chews on the one(s) it ran into and has `kvar` movement points left after this tick;
   *  it stays on its own cell and leans towards `mot`, the cell it was heading for. */
  | { typ: "tugg"; k: number; gruppIndex: number; kvar: number; mot: PacmanPos }
  /** A fight ended in a draw: this pacman is out, nobody ate it. */
  | { typ: "lika"; k: number; offer: number; x: number; y: number }
  /** A used power-up reappears somewhere else. */
  | { typ: "nyPowerup"; k: number; x: number; y: number; powerup: PacmanPowerupTyp }
  /** y: the pacman turns around (after the last move). */
  | { typ: "vand"; gruppIndex: number; till: PacmanPjas }
  | { typ: "teleport"; gruppIndex: number; fran: PacmanPos; till: PacmanPjas }
  /** An eaten pacman comes back somewhere else. */
  | { typ: "aterfor"; gruppIndex: number; till: PacmanPjas };

export interface PacmanDrag {
  /** Epoch ms when "Kör" was pressed. */
  start: number;
  /** Every pacman and power-up as they stood BEFORE the walk (the replay's start). */
  startPjaser: (PacmanPjas | null)[];
  startPowerups: PacmanPowerup[];
  /** The longest walk in moves — drives the animation length. */
  maxSteg: number;
  handelser: PacmanHandelse[];
}

export interface PacmanPowerup {
  x: number;
  y: number;
  typ: PacmanPowerupTyp;
}

export interface PacmanState extends GemensamState {
  fas: PacmanFas;
  /** 1-based round number; 0 before the game starts. */
  omgang: number;
  antalOmgangar: number;
  daIndex: number | null;
  dragnaDa: number[];
  labyrint: PacmanLabyrint;
  /** One pacman per group (same index as grupper); null while eaten and not yet back. */
  pjaser: (PacmanPjas | null)[];
  powerups: PacmanPowerup[];
  val: PacmanVal[];
  /** The Steg each group picked for the NEXT question — chosen on the facit screen, together with the route
   *  (Erik 2026-10-05: the only time players choose is after the result). Carried into `val` by `nasta`.
   *  Absent in games saved before the change: read it through `nastaValFor`. */
  nastaVal?: PacmanVal[];
  fragaOppnad: number | null;
  utfall: PacmanUtfall[] | null;
  /** Bonus moves earned last round, per group, waiting for this round's result. */
  bonus: PacmanStegPost[][];
  /** This round's moves per group and where they came from (set at facit). */
  stegRad: PacmanStegPost[][];
  stat: PacmanStat[];
  drag: PacmanDrag | null;
}

// ---------------------------------------------------------------------------
// Questions as sent to clients
// ---------------------------------------------------------------------------

export type SpelSvarTyp =
  | "flerval"
  | "flerval-multi"
  | "rangordna"
  | "para-ihop"
  | "svarsruta"
  | "slider";

/** A question WITHOUT facit — safe to send to students during "fraga". */
export interface SpelUppgiftVy {
  id: string;
  svarTyp: SpelSvarTyp;
  fraga: string;
  niva: Niva;
  /** flerval / flerval-multi: alternative texts in display order (index = ordning). */
  alternativ?: { ordning: number; text: string }[];
  /** rangordna */
  poster?: string[];
  etikettStart?: string;
  etikettSlut?: string;
  /** para-ihop */
  vanster?: string[];
  hoger?: string[];
  /** slider */
  slider?: { min: number; max: number; steg: number };
  /** svarsruta: how to write the answer. */
  svarsformatInstruktion?: string | null;
  svarsformatExempel?: string | null;
}

export interface SpelFacitVy {
  /** Human-readable correct answer, one line per row (LaTeX allowed). */
  rader: string[];
  losning: string | null;
}

// ---------------------------------------------------------------------------
// Views returned by GET /api/spel/[id]/state
// ---------------------------------------------------------------------------

export interface SpelDeltagareVy {
  id: string;
  smeknamn: string;
  /** Summario avatar 0–9 (SpelDeltagare.avatar; missing ⇒ a default from the id). */
  avatar?: number;
  smeknamnStatus: "ok" | "nekad";
  gruppIndex: number;
  /** Real name — teacher view only. */
  namn?: string;
  elevId?: string;
}

export interface SpelRutaVy {
  key: string;
  daIndex: number;
  niva: Niva;
  varde: number;
  forbrukad: boolean;
  /** Teacher view only: false when no question could be drawn for the square. */
  harFraga?: boolean;
}

export interface SpelVyBas<T extends SpelTyp = SpelTyp, S extends GemensamState = GemensamState> {
  id: string;
  spelTyp: T;
  status: SpelStatus;
  version: number;
  klassId: string;
  config: Pick<SpelConfig, "delavsnitt" | "varden" | "gruppForm"> & {
    visaStallning: Stallningsvisning;
    antalOmgangar?: number;
    /** Timed game: the play time in minutes (spel-spec §28). */
    tidMinuter?: number;
    lastaSteg?: Niva[];
    /** PacMathMan: points count (the number of other pacmen eaten). */
    pacmanPoang?: boolean;
  };
  state: S;
  rutor: SpelRutaVy[];
  deltagare: SpelDeltagareVy[];
  /** Question in play; present from "fraga" onwards (teacher: from "satsa"). */
  uppgift: SpelUppgiftVy | null;
  /** Present in "facit" (teacher: from "satsa"). */
  facit: SpelFacitVy | null;
  /** Number of participants that have answered in the current round. */
  antalSvarat: number;
  /** Student view, team games, "fraga" only: what the teammates have
   *  answered so far (raw answers; never whether they are right). */
  lagSvar?: { deltagareId: string; svar: string }[];
  /** Echoed by the poll (?p=) so an unchanged game costs nothing —
   *  participants (+ answers for the teacher), see lib/spel/poll-cache.ts. */
  puls: string;
  /** Milliseconds left to answer, measured when the view was built (relative,
   *  so client clock skew does not matter). null = no running time limit. */
  fragaKvarMs: number | null;
  /** Timed game: ms of play time left, relative like fragaKvarMs (null = not timed / not started). The views
   *  count it down locally and show it every ten seconds. */
  speltidKvarMs?: number | null;
  /** Stegkampen/Summario: the Steg that have a question in the current round. */
  tillgangligaSteg?: Niva[];
  /** Summario: ms left of the powerup choice window, relative (null = none open). */
  powerupKvarMs?: number | null;
  /** Summario: ms since the dice were rolled (null outside "drag") — a
   *  reloaded projector does not replay a movement that already played. */
  dragAlderMs?: number | null;
  /** Stegkampen/Summario, teacher and projector: the round's question + facit per Steg.
   *  Filled from "val" for the teacher, from "fraga" for the projector view. */
  uppgifter?: Partial<Record<Niva, { uppgift: SpelUppgiftVy; facit: SpelFacitVy }>>;
  /** Pacman, facit phase: each group's planning progress (see PacmanPlanVy). Not part of the
   *  state: saving a plan must not bump the version for everyone. */
  pacmanPlan?: PacmanPlanVy[];
  /** Summario, facit phase: each group's planned route (see SummarioPlanVy). Not part of the state, like pacmanPlan. */
  summarioPlan?: SummarioPlanVy[];
  /** Matte-hjulet: ms since the spin started (null when no spin runs) and ms
   *  until answering opens (≤ 0 once open) — relative, see dragAlderMs. */
  snurrAlderMs?: number | null;
  /** Vanligt Quiz, student view: the own group's three questions this round (the leader's answers in a
   *  team), facit and right/wrong from "facit". Teacher and projector get `uppgifter` per Steg instead. */
  quiz?: QuizRundaVy;
  /** Matte-hjulet: each group's three questions this round. Teacher and
   *  projector: every group (with facit). Student: the own group only, facit
   *  from "facit", plus the own answers. */
  hjul?: HjulRundaVy[];
  /** A voted game (spel-spec §20). While `vinnare` is null, `spelTyp` is
   *  only a placeholder — views show the vote instead of the game. */
  rostning?: SpelRostning & {
    /** Votes so far per alternative — teacher and projector only. */
    roster?: Partial<Record<SpelTyp, number>>;
  };
}

/** The own group's questions in a Vanligt Quiz round (same shape as one Matte-hjulet group's). */
export type QuizRundaVy = Pick<HjulRundaVy, "perSteg">;

/** One group's questions in a Matte-hjulet round. */
export interface HjulRundaVy {
  gruppIndex: number;
  perSteg: Partial<
    Record<
      Niva,
      {
        uppgift: SpelUppgiftVy;
        facit: SpelFacitVy | null;
        /** Student view: the group's answer to this question (the leader's in
         *  a team, else the student's own) and — from facit — whether it was right. */
        svar?: string | null;
        ratt?: boolean | null;
      }
    >
  >;
}

export interface SpelSvarLarareVy {
  deltagareId: string;
  /** Stegkampen: which Steg's question the answer belongs to. */
  niva?: Niva;
  svar: string;
  svarText: string;
  ratt: boolean;
  tidMs: number;
}

interface LarareFalt {
  roll: "larare";
  /** Every answer in the current round (teacher sees individual answers). */
  svar: SpelSvarLarareVy[];
  /** Invited students that have not joined yet. */
  ejAnslutna: { elevId: string; namn: string }[];
}

interface ElevFalt {
  roll: "elev";
  /** null = not joined yet (show the nickname form). */
  jag: {
    deltagareId: string;
    smeknamn: string;
    smeknamnStatus: "ok" | "nekad";
    gruppIndex: number;
    /** Own answer this round, if any. */
    svar: string | null;
    /** Only in "facit". */
    ratt: boolean | null;
    /** The game this student voted for (voted games only). */
    rost: SpelTyp | null;
  } | null;
  /** The teacher removed this student from the game. */
  utkastad?: boolean;
}

export type OpardyLarareVy = SpelVyBas<"matteopardy", OpardyState> & LarareFalt;
export type OpardyElevVy = SpelVyBas<"matteopardy", OpardyState> & ElevFalt;
export type StegkampLarareVy = SpelVyBas<"stegkampen", StegkampState> & LarareFalt;
export type StegkampElevVy = SpelVyBas<"stegkampen", StegkampState> & ElevFalt;

export type SummarioLarareVy = SpelVyBas<"summario", SummarioState> & LarareFalt;
export type SummarioElevVy = SpelVyBas<"summario", SummarioState> & ElevFalt;

export type HjuletLarareVy = SpelVyBas<"mattehjulet", HjuletState> & LarareFalt;
export type HjuletElevVy = SpelVyBas<"mattehjulet", HjuletState> & ElevFalt;

export type QuizLarareVy = SpelVyBas<"quiz", QuizState> & LarareFalt;
export type QuizElevVy = SpelVyBas<"quiz", QuizState> & ElevFalt;

export type PacmanLarareVy = SpelVyBas<"pacman", PacmanState> & LarareFalt;
export type PacmanElevVy = SpelVyBas<"pacman", PacmanState> & ElevFalt;

export type SpelLarareVy = OpardyLarareVy | StegkampLarareVy | SummarioLarareVy | HjuletLarareVy | PacmanLarareVy | QuizLarareVy;
export type SpelElevVy = OpardyElevVy | StegkampElevVy | SummarioElevVy | HjuletElevVy | PacmanElevVy | QuizElevVy;
export type SpelVy = SpelLarareVy | SpelElevVy;

/** Summary used by the student header icon/popup (via aktiv-lektion). */
export interface AktivtSpel {
  id: string;
  spelTyp: SpelTyp;
  status: "lobby" | "pagar";
  deltar: boolean;
  /** The students are voting which game to play (the popup says so). */
  rostning: boolean;
}

export const SPEL_NAMN: Record<SpelTyp, string> = {
  matteopardy: "Matte-opardy",
  stegkampen: "Satsa Allt",
  summario: "Summario",
  mattehjulet: "Spin to Win",
  pacman: "PacMathMan",
  quiz: "Quiz",
};

export const NIVAER: Niva[] = ["E", "C", "A"];

export function rutaKey(daIndex: number, niva: Niva): string {
  return `${daIndex}-${niva}`;
}

export function parseRutaKey(key: string): { daIndex: number; niva: Niva } | null {
  const m = /^(\d+)-(E|C|A)$/.exec(key);
  if (!m) return null;
  return { daIndex: Number(m[1]), niva: m[2] as Niva };
}
